import { confirmToast } from "../../utils/toast";
import React, { useState, useEffect, useMemo } from "react";
import axios from "../../utils/axiosConfig";
import {
  collectValidationErrors,
  showValidationAlert,
  validateEmail,
  validateMobile,
  validatePhone,
  validateWebsite
} from "../../utils/fieldValidation";

const ShipperMaster = () => {

  const initialForm = {
    ShipperName: "",
    FullName: "",
    TaxNo: "",
    OpeningBalanceAsOn20260401: "",
    Address: "",
    Area: "",
    City: "",
    State: "",
    Country: "",
    Zip: "",
    ISD_CODE: "",
    Phone: "",
    Fax: "",
    Mobile: "",
    Email: "",
    ContactPerson: "",
    BankStatus: "",
    Remark: "",
    ShippingInstructions: "",
    InTime: "",
    OutTime: "",
    UserId: "",
    Password: "",
    IsLoginReq: false,
    Fax_ISD_Code: "",
    Website: ""
  };

  const initialContact = {
    ContactPerson: "",
    ContactNo: "",
    Designation: "",
    Department: "",
    ExtNo: "",
    MobileNo: "",
    Email: "",
    ISD_Code: ""
  };

  const [form, setForm] = useState(initialForm);
  const [contactForm, setContactForm] = useState(initialContact);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [items, setItems] = useState([]);
  const [itemIds, setItemIds] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([])
  const [shippers, setShippers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("list");
  const [search, setSearch] = useState("");
  const [showShipperDetails, setShowShipperDetails] = useState(true);
  const [showContactPersons, setShowContactPersons] = useState(true);
  const [showInterestedItems, setShowInterestedItems] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [modalItemIds, setModalItemIds] = useState([]);
  const [selectedInterestedItemIds, setSelectedInterestedItemIds] = useState([]);
  const [itemModalPage, setItemModalPage] = useState(1);

  /* pagination */
  const [page, setPage] = useState(1);
  const perPage = 5;
  const itemModalRowsPerPage = 8;

  useEffect(() => {
    fetchShippers();
    fetchGroups();
    fetchCountries();
    fetchItems();
  }, []);

  const fetchShippers = async () => {
    const res = await axios.get("/shippers");
    setShippers(res.data);
  };

  const fetchGroups = async () => {
    const res = await axios.get("/contact-groups");
    setGroups(res.data);
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get("/shippers/items");
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItems([]);
    }
  };

  const fetchShipperItems = async (shipperId) => {
    try {
      const res = await axios.get(`/shippers/${shipperId}/items`);
      setItemIds(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setItemIds([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get("/countries");
      setCountries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      alert("Unable to load countries. Please reload the page and try again.");
    }
  }

  const fetchContacts = async (id) => {
    const res = await axios.get(`/shippers/${id}/contacts`);
    setContacts(res.data);
  };

  const handleChange = async e => {

    const { name, value, type, checked } = e.target

    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }))

    if (name === "Country") {
      setForm(prev => ({ ...prev, State: "", City: "" }))
      setStates([]);
      if (!value) return;
      try {
        const res = await axios.get(`/states/by-country/${value}`);
        setStates(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        alert("Unable to load states. Please select the country again.");
      }
    }

    if (name === "State") {
      setForm(prev => ({ ...prev, City: "" }))
    }

  }

  const handleContactChange = e => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleGroup = id => {
    setGroupIds(prev => prev.includes(id)
      ? prev.filter(x => x !== id)
      : [...prev, id]);
  };

  const openItemModal = () => {
    setModalItemIds(itemIds);
    setItemModalPage(1);
    setShowItemModal(true);
  };

  const toggleModalItem = (id) => {
    setModalItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const applyModalItems = () => {
    setItemIds(modalItemIds);
    setSelectedInterestedItemIds((prev) => prev.filter((id) => modalItemIds.includes(id)));
    setShowItemModal(false);
  };

  const toggleInterestedItemSelection = (id) => {
    setSelectedInterestedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const removeSelectedInterestedItems = () => {
    setItemIds((prev) => prev.filter((id) => !selectedInterestedItemIds.includes(id)));
    setSelectedInterestedItemIds([]);
  };

  const resetForm = () => {
    setForm(initialForm);
    setContactForm(initialContact);
    setContacts([]);
    setGroupIds([]);
    setItemIds([]);
    setModalItemIds([]);
    setSelectedInterestedItemIds([]);
    setShowItemModal(false);
    setItemModalPage(1);
    setStates([]);
    setEditingId(null);
    setShowShipperDetails(true);
    setShowContactPersons(true);
    setShowInterestedItems(true);
  };

  const openNewShipper = () => {
    resetForm();
    setActiveTab("form");
  };

  const backToList = () => {
    resetForm();
    setActiveTab("list");
  };

  const validateContactRows = (rows) => {
    return rows.flatMap((contact, index) => {
      const label = contact.ContactPerson?.trim() || `Contact ${index + 1}`;
      return collectValidationErrors([
        validatePhone(contact.ContactNo, `${label} phone`),
        validateMobile(contact.MobileNo, `${label} mobile`),
        validateEmail(contact.Email, `${label} email`)
      ]);
    });
  };

  const validateShipperForm = () => {
    const errors = collectValidationErrors([
      validatePhone(form.Phone, "Shipper phone"),
      validatePhone(form.Fax, "Shipper fax"),
      validateMobile(form.Mobile, "Shipper mobile"),
      validateEmail(form.Email, "Shipper email"),
      validateWebsite(form.Website, "Shipper website"),
      ...validateContactRows(contacts)
    ]);

    return !showValidationAlert(errors);
  };

  const validateContactForm = () => {
    const errors = collectValidationErrors([
      validatePhone(contactForm.ContactNo, "Contact phone"),
      validateMobile(contactForm.MobileNo, "Contact mobile"),
      validateEmail(contactForm.Email, "Contact email")
    ]);

    return !showValidationAlert(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateShipperForm()) return;

    try {
      let shipperId = editingId;

      if (shipperId) {
        await axios.put(`/shippers/${shipperId}`, form);
      } else {
        const res = await axios.post("/shippers", form);
        shipperId = res.data.ShipperID;
      }

      if (shipperId) {
        await axios.post(`/shippers/${shipperId}/groups`, { groupIds });
        await axios.post(`/shippers/${shipperId}/items`, { itemIds });

        const pendingContacts = contacts.filter(contact => contact._tempId);
        for (const contact of pendingContacts) {
          const { _tempId, ...payload } = contact;
          await axios.post(`/shippers/${shipperId}/contacts`, payload);
        }
      }

      alert(editingId ? "Shipper updated successfully" : "Shipper saved successfully");
      fetchShippers();
      resetForm();
      setActiveTab("list");
    } catch (err) {
      alert(err.response?.data || err.message || "Error saving shipper");
    }
  };

  const editShipper = async (row) => {

    setShowShipperDetails(true);
    setShowContactPersons(true);
    setShowInterestedItems(true);
    setForm({
      ShipperName: row.ShipperName || "",
      FullName: row.FullName || "",
      TaxNo: row.TaxNo || "",
      OpeningBalanceAsOn20260401: row.OpeningBalanceAsOn20260401 ?? "",
      Address: row.Address || "",
      Area: row.Area || "",
      City: row.City || "",
      State: row.State || "",
      Country: row.Country || "",
      Zip: row.Zip || "",
      ISD_CODE: row.ISD_CODE || "",
      Phone: row.Phone || "",
      Fax: row.Fax || "",
      Mobile: row.Mobile || "",
      Email: row.Email || "",
      ContactPerson: row.ContactPerson || "",
      BankStatus: row.BankStatus || "",
      Remark: row.Remark || "",
      ShippingInstructions: row.ShippingInstructions || "",
      InTime: row.InTime || "",
      OutTime: row.OutTime || "",
      UserId: row.UserId || "",
      Password: row.Password || "",
      IsLoginReq: !!row.IsLoginReq,
      Fax_ISD_Code: row.Fax_ISD_Code || "",
      Website: row.Website || ""
    });
    setEditingId(row.ShipperID);
    setActiveTab("form");

    fetchContacts(row.ShipperID);
    fetchShipperItems(row.ShipperID);

    try {
      if (row.Country) {
        const statesRes = await axios.get(`/states/by-country/${row.Country}`);
        setStates(statesRes.data);
      }

      const groupRes = await axios.get(`/shippers/${row.ShipperID}/groups`);
      setGroupIds(groupRes.data);
    } catch (e) {
      console.warn("Error loading related data:", e.message);
    }

  };

  const deleteShipper = async (id) => {
    if (await confirmToast("Delete shipper?")) {
      await axios.delete(`/shippers/${id}`);
      fetchShippers();
    }
  };

  const addContact = async () => {
    if (!contactForm.ContactPerson) {
      alert("Person name is required");
      return;
    }

    if (!validateContactForm()) return;

    if (!editingId) {
      setContacts(prev => [
        ...prev,
        {
          ...contactForm,
          _tempId: `${Date.now()}-${prev.length}`
        }
      ]);
      setContactForm(initialContact);
      return;
    }

    try {
      await axios.post(`/shippers/${editingId}/contacts`, contactForm);
      fetchContacts(editingId);
      setContactForm(initialContact);
    } catch (err) {
      alert("Error: " + (err.response?.data || err.message));
    }
  };

  const deleteContact = async (contact) => {
    if (contact._tempId) {
      setContacts(prev => prev.filter(item => item._tempId !== contact._tempId));
      return;
    }

    if (await confirmToast("Delete contact?")) {
      try {
        await axios.delete(`/shippers/contacts/${contact.ContactId}`);
        fetchContacts(editingId);
      } catch (err) {
        alert("Error deleting contact: " + (err.response?.data || err.message));
      }
    }
  };

  /* pagination logic */
  const filteredShippers = shippers.filter((shipper) => {
    const term = search.trim().toLowerCase();
    if (!term) return true;

    return [
      shipper.ShipperName,
      shipper.FullName,
      shipper.TaxNo,
      shipper.City,
      shipper.Phone,
      shipper.Email
    ].some((value) => String(value || "").toLowerCase().includes(term));
  });

  const last = page * perPage;
  const first = last - perPage;
  const rows = filteredShippers.slice(first, last);
  const totalPages = Math.ceil(filteredShippers.length / perPage);
  const interestedItems = useMemo(
    () => items.filter((item) => itemIds.includes(item.ItemID)),
    [items, itemIds]
  );
  const itemModalTotalPages = Math.max(1, Math.ceil(items.length / itemModalRowsPerPage));
  const modalItems = items.slice(
    (itemModalPage - 1) * itemModalRowsPerPage,
    itemModalPage * itemModalRowsPerPage
  );
  const allInterestedItemsSelected = interestedItems.length > 0 && interestedItems.every(
    (item) => selectedInterestedItemIds.includes(item.ItemID)
  );
  const allModalPageItemsSelected = modalItems.length > 0 && modalItems.every(
    (item) => modalItemIds.includes(item.ItemID)
  );

  const toggleAllInterestedItems = () => {
    setSelectedInterestedItemIds(
      allInterestedItemsSelected ? [] : interestedItems.map((item) => item.ItemID)
    );
  };

  const toggleAllModalPageItems = () => {
    const pageIds = modalItems.map((item) => item.ItemID);
    setModalItemIds((prev) => allModalPageItemsSelected
      ? prev.filter((id) => !pageIds.includes(id))
      : [...new Set([...prev, ...pageIds])]
    );
  };

  return (
    <div className="container-fluid">

      <h4 className="fw-bold mb-3">Shipper Master</h4>

      <div className="card shadow-sm border-0">

        <div className="card-header bg-white">
          <ul className="nav nav-tabs card-header-tabs">

            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === "list" ? "active" : ""}`}
                onClick={() => setActiveTab("list")}>
                Shipper List
              </button>
            </li>

            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === "form" ? "active" : ""}`}
                onClick={() => {
                  if (activeTab !== "form") openNewShipper();
                }}>
                {editingId ? "Edit Shipper" : "Add Shipper"}
              </button>
            </li>

          </ul>
        </div>

        <div className="card-body">

          {/* FORM */}
          {activeTab === "form" && (

            <form onSubmit={handleSubmit}>

              <div className="border rounded bg-white mb-3 master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  role="button"
                  tabIndex="0"
                  onClick={() => setShowShipperDetails(show => !show)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setShowShipperDetails(show => !show);
                    }
                  }}
                >
                  <span>Shipper Information</span>
                  <span>{showShipperDetails ? "▲" : "▼"}</span>
                </div>

                {showShipperDetails && (
                  <div className="p-3">
                    <div className="row g-3">

                {/* Short Company Name */}
                <div className="col-md-4">
                  <label className="form-label">Short Company Name *</label>
                  <input
                    name="ShipperName"
                    className="form-control"
                    value={form.ShipperName}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Full Company Name */}
                <div className="col-md-4">
                  <label className="form-label">Full Company Name</label>
                  <input
                    name="FullName"
                    className="form-control"
                    value={form.FullName}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Tax No</label>
                  <input
                    name="TaxNo"
                    className="form-control"
                    value={form.TaxNo}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Opening Balance as on 1st Apr 2026</label>
                  <input
                    type="number"
                    step="0.01"
                    name="OpeningBalanceAsOn20260401"
                    className="form-control"
                    value={form.OpeningBalanceAsOn20260401}
                    onChange={handleChange}
                  />
                </div>

                {/* Address */}
                <div className="col-md-12">
                  <label className="form-label">Address</label>
                  <textarea
                    name="Address"
                    className="form-control"
                    rows="2"
                    value={form.Address}
                    onChange={handleChange}
                  />
                </div>
                {/* Country */}
                <div className="col-md-3">
                  <label className="form-label">Country</label>
                  <select
                    name="Country"
                    className="form-select"
                    value={form.Country}
                    onChange={handleChange}
                  >

                    <option value="">Select Country</option>

                    {countries.map(c => (
                      <option key={c.CountryId} value={c.CountryId}>
                        {c.CountryName}
                      </option>
                    ))}

                  </select>
                </div>
                {/* State */}
                <div className="col-md-3">
                  <label className="form-label">State</label>
                  <select
                    name="State"
                    className="form-select"
                    value={form.State}
                    onChange={handleChange}
                  >

                    <option value="">Select State</option>

                    {states.map(s => (
                      <option key={s.StateId} value={s.StateId}>
                        {s.StateName}
                      </option>
                    ))}

                  </select>
                </div>
                {/* City */}
                <div className="col-md-3">
                  <label className="form-label">City</label>
                  <input
                    name="City"
                    className="form-control"
                    value={form.City}
                    onChange={handleChange}
                    placeholder="Enter City"
                  />
                </div>





                {/* Zip */}
                <div className="col-md-3">
                  <label className="form-label">Zip</label>
                  <input
                    name="Zip"
                    className="form-control"
                    value={form.Zip}
                    onChange={handleChange}
                  />
                </div>

                {/* Phone */}
                <div className="col-md-3">
                  <label className="form-label">Phone</label>
                  <input
                    name="Phone"
                    className="form-control"
                    value={form.Phone}
                    onChange={handleChange}
                    inputMode="tel"
                  />
                </div>

                {/* Fax */}
                <div className="col-md-3">
                  <label className="form-label">Fax</label>
                  <input
                    name="Fax"
                    className="form-control"
                    value={form.Fax}
                    onChange={handleChange}
                    inputMode="tel"
                  />
                </div>

                {/* Mobile */}
                <div className="col-md-3">
                  <label className="form-label">Mobile</label>
                  <input
                    name="Mobile"
                    className="form-control"
                    value={form.Mobile}
                    onChange={handleChange}
                    inputMode="tel"
                  />
                </div>

                {/* Email */}
                <div className="col-md-3">
                  <label className="form-label">Email</label>
                  <input
                    name="Email"
                    type="email"
                    className="form-control"
                    value={form.Email}
                    onChange={handleChange}
                  />
                </div>

                {/* Website */}
                <div className="col-md-6">
                  <label className="form-label">Website</label>
                  <input
                    name="Website"
                    className="form-control"
                    value={form.Website}
                    onChange={handleChange}
                  />
                </div>

                {/* Shipping Instruction */}
                <div className="col-md-6">
                  <label className="form-label">Shipping Instruction</label>
                  <textarea
                    name="ShippingInstructions"
                    className="form-control"
                    rows="2"
                    value={form.ShippingInstructions}
                    onChange={handleChange}
                  />
                </div>

                {/* Remarks */}
                <div className="col-md-12">
                  <label className="form-label">Remarks</label>
                  <textarea
                    name="Remark"
                    className="form-control"
                    rows="2"
                    value={form.Remark}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12">

                  <label className="form-label fw-semibold">Contact Group</label>

                  <div className="border rounded p-2" style={{ maxHeight: 120 }}>

                    <div className="row">

                      {groups.map(g => (

                        <div key={g.GroupId} className="col-md-3">

                          <div className="form-check">

                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={groupIds.includes(g.GroupId)}
                              onChange={() => toggleGroup(g.GroupId)}
                            />

                            <label className="form-check-label">
                              {g.GroupDesc}
                            </label>

                          </div>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

                    </div>
                  </div>
                )}
              </div>

              <div className="border rounded bg-white mb-3 master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  role="button"
                  tabIndex="0"
                  onClick={() => setShowContactPersons(show => !show)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setShowContactPersons(show => !show);
                    }
                  }}
                >
                  <span>Contact Persons</span>
                  <span>{showContactPersons ? "▲" : "▼"}</span>
                </div>

                {showContactPersons && (
                  <div className="p-3">
                    <div className="row g-3 mb-3">

                <div className="col-md-2">
                  <label className="form-label">Contact Person *</label>
                  <input className="form-control"
                    name="ContactPerson"
                    value={contactForm.ContactPerson}
                    onChange={handleContactChange} />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Designation</label>
                  <input className="form-control"
                    name="Designation"
                    value={contactForm.Designation}
                    onChange={handleContactChange} />
                </div>

                <div className="col-md-1">
                  <label className="form-label">Department</label>
                  <input className="form-control"
                    name="Department"
                    value={contactForm.Department}
                    onChange={handleContactChange} />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Phone</label>
                  <input className="form-control"
                    name="ContactNo"
                    value={contactForm.ContactNo}
                    onChange={handleContactChange}
                    inputMode="tel" />
                </div>

                <div className="col-md-1">
                  <label className="form-label">Mobile</label>
                  <input className="form-control"
                    name="MobileNo"
                    value={contactForm.MobileNo}
                    onChange={handleContactChange}
                    inputMode="tel" />
                </div>

                <div className="col-md-2">
                  <label className="form-label">Email</label>
                  <input className="form-control"
                    name="Email"
                    type="email"
                    value={contactForm.Email}
                    onChange={handleContactChange} />
                </div>

                <div className="col-md-1">
                  <label className="form-label">Extension</label>
                  <input className="form-control"
                    name="ExtNo"
                    value={contactForm.ExtNo}
                    onChange={handleContactChange} />
                </div>

                <div className="col-12 d-flex justify-content-end">
                  <button type="button"
                    className="btn btn-success"
                    onClick={addContact}>Add Contact</button>
                </div>

                    </div>

                    <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Person</th>
                    <th>Dept</th>
                    <th>Designation</th>
                    <th>Phone</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>Ext</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>

                  {contacts.map(c => (
                    <tr key={c.ContactId || c._tempId}>
                      <td>{c.ContactPerson}</td>
                      <td>{c.Department}</td>
                      <td>{c.Designation}</td>
                      <td>{c.ContactNo}</td>
                      <td>{c.MobileNo}</td>
                      <td>{c.Email}</td>
                      <td>{c.ExtNo}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => deleteContact(c)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}

                </tbody>
              </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="border rounded bg-white mb-3 master-collapse-section">
                <div
                  className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
                  role="button"
                  tabIndex="0"
                  onClick={() => setShowInterestedItems((show) => !show)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      setShowInterestedItems((show) => !show);
                    }
                  }}
                >
                  <span>Interested Items</span>
                  <span>{showInterestedItems ? "▲" : "▼"}</span>
                </div>

                {showInterestedItems && (
                  <div className="p-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div className="text-muted">
                        Items currently marked as interested for this shipper
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm me-2"
                          disabled={selectedInterestedItemIds.length === 0}
                          onClick={removeSelectedInterestedItems}
                        >
                          Remove Selected
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={openItemModal}
                        >
                          Add Items
                        </button>
                      </div>
                    </div>
                    <div className="table-responsive border rounded interested-items-panel">
                      <table className="table table-bordered table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center" style={{ width: 56 }}>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                aria-label="Select all interested items"
                                checked={allInterestedItemsSelected}
                                onChange={toggleAllInterestedItems}
                                disabled={interestedItems.length === 0}
                              />
                            </th>
                            <th>Item Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {interestedItems.length === 0 ? (
                            <tr>
                              <td colSpan="2" className="text-center text-muted py-3">
                                No interested items selected.
                              </td>
                            </tr>
                          ) : interestedItems.map((item) => (
                            <tr key={item.ItemID}>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  aria-label={`Select ${item.ItemName || item.ItemCode}`}
                                  checked={selectedInterestedItemIds.includes(item.ItemID)}
                                  onChange={() => toggleInterestedItemSelection(item.ItemID)}
                                />
                              </td>
                              <td>{item.ItemName || item.ItemCode}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 text-end">
                <button className="btn btn-success me-2">
                  {editingId ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={backToList}>
                  Back to List
                </button>
              </div>

            </form>
          )}

          {/* LIST */}

          {activeTab === "list" && (

            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h5 className="mb-0">Shipper List</h5>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={openNewShipper}
                >
                  Add Shipper
                </button>
              </div>

              <div className="mb-3">
                <input
                  type="search"
                  className="form-control"
                  style={{ maxWidth: 420 }}
                  placeholder="Search shippers..."
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Short Company Name</th>
                      <th>Full Company Name</th>
                      <th>Tax No</th>
                      <th>City</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-3">
                          No shippers found.
                        </td>
                      </tr>
                    ) : rows.map(r => (
                      <tr key={r.ShipperID}>
                        <td>{r.ShipperName}</td>
                        <td>{r.FullName}</td>
                        <td>{r.TaxNo}</td>
                        <td>{r.City}</td>
                        <td>{r.Phone}</td>
                        <td>{r.Email}</td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => editShipper(r)}>
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteShipper(r.ShipperID)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span>
                    Showing {first + 1} to {Math.min(last, filteredShippers.length)} of {filteredShippers.length}
                  </span>
                  <ul className="pagination mb-0">
                    {Array.from({ length: totalPages }, (_, i) => (
                      <li key={i}
                        className={`page-item ${page === i + 1 ? "active" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setPage(i + 1)}>
                          {i + 1}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            </>

          )}

        </div>
      </div>

      {showItemModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="shipperInterestedItemsModalTitle">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="shipperInterestedItemsModalTitle">Select Interested Items</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowItemModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="text-center" style={{ width: 56 }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              aria-label="Select all items on this page"
                              checked={allModalPageItemsSelected}
                              onChange={toggleAllModalPageItems}
                              disabled={modalItems.length === 0}
                            />
                          </th>
                          <th style={{ width: "180px" }}>Item Code</th>
                          <th>Item Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modalItems.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center text-muted py-4">No items found.</td>
                          </tr>
                        ) : modalItems.map((item) => (
                          <tr key={item.ItemID}>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                aria-label={`Select ${item.ItemName || item.ItemCode}`}
                                checked={modalItemIds.includes(item.ItemID)}
                                onChange={() => toggleModalItem(item.ItemID)}
                              />
                            </td>
                            <td>{item.ItemCode}</td>
                            <td>{item.ItemName || item.ItemCode}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={itemModalPage === 1}
                      onClick={() => setItemModalPage((currentPage) => Math.max(1, currentPage - 1))}
                    >
                      Previous
                    </button>
                    <span className="small text-muted">
                      Page {itemModalPage} of {itemModalTotalPages}
                    </span>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      disabled={itemModalPage === itemModalTotalPages}
                      onClick={() => setItemModalPage((currentPage) => Math.min(itemModalTotalPages, currentPage + 1))}
                    >
                      Next
                    </button>
                  </div>
                  <div>
                    <button type="button" className="btn btn-secondary me-2" onClick={() => setShowItemModal(false)}>
                      Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={applyModalItems}>
                      Add Selected ({modalItemIds.length})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );

};

export default ShipperMaster;
